/**
 * ScoreRing — 0–100 radial gauge with title and status label
 *
 * Layouts:
 *  - `horizontal` — compact row: ring + label + subtitle side-by-side.
 *  - `vertical`   — hero: title on top, large ring below, label underneath.
 *
 * Tiers default to PROGRESS_THRESHOLDS (3-tier emerald/amber/orange). Pass a
 * custom `tiers` array to override colors, labels, or breakpoints.
 */
import { ReactNode } from 'react';
import { RadialBarChart, RadialBar, PolarAngleAxis } from 'recharts';
import { getProgressTier, type ProgressTier } from './colors';

export interface ScoreRingProps {
    title: string;
    score: number;
    icon?: ReactNode;
    subtitle?: ReactNode;
    layout?: 'horizontal' | 'vertical';
    className?: string;
    tiers?: readonly ProgressTier[];
}

export default function ScoreRing({
    title,
    score,
    icon,
    subtitle,
    layout = 'horizontal',
    className,
    tiers,
}: ScoreRingProps) {
    const { color, label } = getProgressTier(score, tiers);
    const data = [{ value: score }];
    const isVertical = layout === 'vertical';

    const ringSize = isVertical ? 160 : 100;
    const ringCenter = ringSize / 2;
    const innerR = isVertical ? 60 : 36;
    const outerR = isVertical ? 76 : 48;

    return (
        <div
            className={`flex gap-5 ${
                isVertical
                    ? 'flex-col items-center justify-center text-center'
                    : 'items-center justify-center'
            } ${className ?? ''}`}
        >
            {isVertical && (
                <div className="flex items-center gap-2 text-xs font-medium tracking-wide text-muted-foreground">
                    {icon && (
                        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-white/5">
                            {icon}
                        </div>
                    )}
                    {title}
                </div>
            )}
            <div
                className="relative shrink-0"
                style={{ height: ringSize, width: ringSize }}
            >
                <RadialBarChart
                    width={ringSize}
                    height={ringSize}
                    cx={ringCenter}
                    cy={ringCenter}
                    innerRadius={innerR}
                    outerRadius={outerR}
                    barSize={isVertical ? 14 : 10}
                    data={data}
                    startAngle={90}
                    endAngle={-270}
                >
                    <PolarAngleAxis
                        type="number"
                        domain={[0, 100]}
                        angleAxisId={0}
                        tick={false}
                    />
                    <RadialBar
                        dataKey="value"
                        cornerRadius={5}
                        fill={color}
                        background={{ fill: 'rgba(255,255,255,0.06)' }}
                    />
                </RadialBarChart>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span
                        className={`font-bold tabular-nums ${
                            isVertical ? 'text-2xl' : 'text-lg'
                        }`}
                        style={{ color }}
                    >
                        {score}%
                    </span>
                </div>
            </div>
            <div className="min-w-0">
                {!isVertical && (
                    <div className="flex items-center gap-1.5 text-xs font-medium tracking-wide text-muted-foreground">
                        {icon}
                        {title}
                    </div>
                )}
                <span
                    className="mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium"
                    style={{
                        backgroundColor: `${color}15`,
                        color,
                    }}
                >
                    {label}
                </span>
                {subtitle && (
                    <div className="mt-1.5 text-xs text-subtle-foreground">
                        {subtitle}
                    </div>
                )}
            </div>
        </div>
    );
}
